import { nextCustomProductNumber } from './productNumbering';

const cloneValue = (value) => (
  value && typeof value === 'object' ? { ...value } : value
);

// Single source of truth for the copy behaviour described in
// product-module-analysis.md §8.2 (Standard) / §8.3 (Custom). Consumed by
// CopyProductModal.js to render the "what will be copied / cleared / not
// copied" confirmation step, so the UI never drifts from the actual copy
// logic below.
export const STANDARD_COPY_FIELD_BEHAVIOR = [
  { field: 'supplierId', label: 'Supplier', behavior: 'copied' },
  { field: 'supplierItemNumber', label: 'Supplier Item Number', behavior: 'cleared' },
  { field: 'name', label: 'Product Name', behavior: 'suffixed' },
  { field: 'colour', label: 'Colour', behavior: 'cleared' },
  { field: 'categoryId', label: 'Category', behavior: 'copied' },
  { field: 'productFamily', label: 'Product Family', behavior: 'copied' },
  { field: 'description', label: 'Description', behavior: 'copied' },
  { field: 'material', label: 'Material', behavior: 'copied' },
  { field: 'countryOfOrigin', label: 'Country of Origin', behavior: 'copied' },
  { field: 'packaging', label: 'Packaging', behavior: 'copied' },
  { field: 'datasheet', label: 'Datasheet', behavior: 'linked' },
  { field: 'brandingTemplates', label: 'Branding Templates', behavior: 'linked' },
  { field: 'images', label: 'Images', behavior: 'not-copied' },
  { field: 'compliance', label: 'Compliance Documents', behavior: 'inherited' },
];

export const CUSTOM_COPY_FIELD_BEHAVIOR = [
  { field: 'internalNumber', label: 'Internal Number', behavior: 'regenerated' },
  { field: 'supplierId', label: 'Supplier', behavior: 'copied' },
  { field: 'name', label: 'Product Name', behavior: 'suffixed' },
  { field: 'categoryId', label: 'Category', behavior: 'copied' },
  { field: 'specifications', label: 'Specifications', behavior: 'copied' },
  { field: 'materials', label: 'Materials', behavior: 'copied' },
  { field: 'packaging', label: 'Packaging', behavior: 'copied' },
  { field: 'productionNotes', label: 'Production Notes', behavior: 'copied' },
  { field: 'compliance', label: 'Compliance Documents', behavior: 'inherited' },
  { field: 'testReports', label: 'Test Reports', behavior: 'inherited' },
  { field: 'images', label: 'Images', behavior: 'not-copied' },
  { field: 'artwork', label: 'Artwork / Logo Files', behavior: 'not-copied' },
  { field: 'prices', label: 'Prices', behavior: 'reset' },
  { field: 'customerReference', label: 'Customer Reference', behavior: 'cleared' },
];

export const buildStandardCopyDraft = (source) => ({
  productType: 'Standard',
  supplierId: source.supplierId,
  supplierItemNumber: '',
  internalNumber: null,
  name: `${source.name} (copy)`,
  colour: '',
  categoryId: source.categoryId,
  productFamily: source.productFamily || '',
  description: source.description || '',
  material: source.material || '',
  countryOfOrigin: source.countryOfOrigin || '',
  packaging: cloneValue(source.packaging) || {},
  specifications: null,
  productionNotes: null,
  primaryImageId: null,
  status: 'Active',
  copiedFromProductId: source.id,
});

export const buildCustomCopyDraft = (source, existingProducts) => ({
  productType: 'Custom',
  internalNumber: nextCustomProductNumber(existingProducts),
  supplierId: source.supplierId,
  name: `${source.name} (copy)`,
  categoryId: source.categoryId,
  productFamily: source.productFamily || '',
  specifications: source.specifications || '',
  materials: source.materials || '',
  packaging: cloneValue(source.packaging) || {},
  productionNotes: source.productionNotes || '',
  colour: source.colour || '',
  primaryImageId: null,
  status: 'Active',
  copiedFromProductId: source.id,
});

export const buildProductCopyDraft = (source, existingProducts) => (
  source.productType === 'Custom'
    ? buildCustomCopyDraft(source, existingProducts)
    : buildStandardCopyDraft(source)
);

// Documents: images (and, for Custom, artwork/logos) are never copied — wrong
// colour would mislead on a Standard copy, and a previous customer's branding
// would leak on a Custom copy (analysis §4.5). Everything else that still
// applies to the physical article (datasheets, branding templates, packaging
// specs, production notes) is linked onto the new product.
export const copyProductDocuments = (documents, sourceProductId, newProductId, productType) => {
  const excludedTypes = productType === 'Custom'
    ? ['Image', 'Artwork']
    : ['Image'];

  return documents
    .filter((doc) => doc.productId === sourceProductId && !excludedTypes.includes(doc.documentType))
    .map((doc) => ({
      ...doc,
      id: undefined,
      productId: newProductId,
      isPrimaryImage: false,
    }));
};

// Compliance documents always arrive flagged inherited + unverified (analysis
// §4.4 / Q2) — a test report certifies a specific production run, so cloning
// it as "already valid" onto a new product would be a real compliance risk.
export const copyProductCertificates = (certificates, sourceProductId, newProductId) => (
  certificates
    .filter((cert) => cert.scope === 'Product' && cert.productId === sourceProductId)
    .map((cert) => ({
      ...cert,
      id: undefined,
      productId: newProductId,
      inherited: true,
      verifiedBy: null,
      verifiedAt: null,
    }))
);
